import { ImMagicWand } from "react-icons/im"
import { Nav, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap"
import { useEffect, useRef, useState, lazy, Suspense } from "react"
import { Link, useLocation } from "react-router-dom"
import {
    FaBars,
    FaTachometerAlt,
    FaLink,
    FaRss,
    FaCog,
    FaTimes,
    FaRegBell,
    FaRegFileAlt,
    FaExclamationTriangle,
    FaUsers,
    FaHandshake,
    FaExchangeAlt,
    FaKey,
} from "react-icons/fa"
import { BiBarChartAlt2, BiBuilding, BiMouse, BiNetworkChart } from "react-icons/bi"
import { BsFileEarmarkText, BsNewspaper } from "react-icons/bs"

import TransactionReport from "./transaction-report/transaction-report"
import { useAuth } from "../../auth/auth-context"
import ReferenceReport from "./reference-report/reference-report"
import PublisherReport from "./publisher-report/publisher-report"
import CampaignReport from "./campaign-report/campaign-report"
import Feed from "./feed/feed"
import NetworkSettings from "./network-settings/network-settings"
import LinkBuilder from "./link-builder/link-builder"
import Alerts from "./alerts/alerts"
import ScheduledReports from "./scheduled-reports/scheduled-reports"
import { useAppContext } from "../../AppContext"
import ClickReport from "./click-report/click-report"
import EBPristjek from "./eb-pristjek/eb-pristjek"
import LinkScript from "./link-script/link-script"
import useFetch from "../../_fetch"
import UserManagement from "../user-management/user-management"
import AdminHome from "./home/admin-home"
import PartnershipAgreements from "./partnership-agreements/partnership-agreements"
import PartnershipAgreementDetail from "./partnership-agreements/partnership-agreement-detail"
import Conversions from "./conversions/conversions"
import PublisherCampaignUrls from "./publisher-campaign-urls/publisher-campaign-urls"
import PartnershipsPage from "./partnerships/partnerships-page"
import NetworkCredentials from "./data-sync/data-sync"

const Home = lazy(() => import("./home/home"))

export function Dashboard() {
    const loc = useLocation()
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const { isPdf } = useAppContext()
    const isAdmin = user?.isAdmin === true

    const isPublisher = user?.kind === "publisher"
    const hasPartnershipsAccess = !!user?.partnerships?.enabled && (user?.kind === "advertiser" || (isPublisher && !!user?.byClick?.enabled))
    const isPdfRequest = isPdf || new URLSearchParams(loc.search).get("isPdf") === "true"
    const isPartnershipPrintRoute = /^\/(?:link-script\/)?partnerships\/[^/]+\/print$/.test(loc.pathname)

    const [activeTab, setActiveTab] = useState<string>("/")
    const [hasActiveCredentials, setHasActiveCredentials] = useState<boolean>(true)
    const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false)
    const hasCheckedCredentialsRef = useRef(false)

    useEffect(() => {
        const { pathname } = loc

        if (pathname === "/") {
            setActiveTab("/")
        } else if (pathname.startsWith("/d/")) {
            setActiveTab(isAdmin ? "/" : "/")
        } else if (pathname.startsWith("/partnerships") || pathname.startsWith("/link-script/partnerships")) {
            setActiveTab("/partnerships")
        } else if (pathname.startsWith("/link-script")) {
            setActiveTab("/link-script")
        } else if (pathname.startsWith("/eb-pristjek")) {
            setActiveTab("/eb-pristjek")
        } else if (pathname.startsWith("/partnership-agreements")) {
            setActiveTab("/partnership-agreements")
        } else if (pathname.startsWith("/conversions")) {
            setActiveTab("/conversions")
        } else if (pathname.startsWith("/network-credentials")) {
            setActiveTab("/network-credentials")
        } else {
            setActiveTab(pathname)
        }
    }, [loc, isAdmin])

    useEffect(() => {
        setIsMobileNavOpen(false)
    }, [loc.pathname])

    useEffect(() => {
        if (hasCheckedCredentialsRef.current) {
            return
        }

        if (isPdfRequest || isPartnershipPrintRoute) {
            return
        }

        hasCheckedCredentialsRef.current = true

        const fetchHasValidated = async () => {
            try {
                const res = await doFetch("/has-validated-network-credentials")
                if (res.status >= 400) throw await res.text()

                const data = await res.json()
                const validated = data.hasValidatedCredentials

                setHasActiveCredentials(validated)
            } catch (err) {
                console.error("Failed to fetch credentials status:", err)
            }
        }

        fetchHasValidated()
    }, [])

    const handleSelect = (selectedKey: any) => {
        setActiveTab(selectedKey)
        setIsMobileNavOpen(false)
    }

    const adminNavLinks = (
        <>
            <Nav.Link as={Link} to="/" eventKey="/" className="text-dark d-flex align-items-center">
                <FaTachometerAlt className="me-2" /> Home
            </Nav.Link>

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                Management
            </Nav.Item>

            <Nav.Link as={Link} to="/user-management" eventKey="/user-management" className="text-dark d-flex align-items-center">
                <FaUsers className="me-2" /> Users
            </Nav.Link>

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                Partner
            </Nav.Item>

            <Nav.Link as={Link} to="/partnership-agreements" eventKey="/partnership-agreements" className="text-dark d-flex align-items-center">
                <FaHandshake className="me-2" /> Partnerships
            </Nav.Link>

            <Nav.Link as={Link} to="/conversions" eventKey="/conversions" className="text-dark d-flex align-items-center">
                <FaExchangeAlt className="me-2" /> Conversions
            </Nav.Link>

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                Networks
            </Nav.Item>

            <Nav.Link as={Link} to="/publisher-campaign-urls" eventKey="/publisher-campaign-urls" className="text-dark d-flex align-items-center">
                <FaLink className="me-2" /> Campaigns
            </Nav.Link>

            <Nav.Link as={Link} to="/network-credentials/credentials" eventKey="/network-credentials" className="text-dark d-flex align-items-center">
                <FaKey className="me-2" /> Network credentials
            </Nav.Link>

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                EB
            </Nav.Item>

            <Nav.Link as={Link} to="/eb-pristjek/products" eventKey="/eb-pristjek" className="text-dark d-flex align-items-center">
                <BsNewspaper className="me-2" /> Pristjek
            </Nav.Link>
        </>
    )

    const defaultNavLinks = (
        <>
            <Nav.Link as={Link} to="/" eventKey="/" className="text-dark d-flex align-items-center">
                <FaTachometerAlt className="me-2" /> Dashboard
            </Nav.Link>

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                Reports
            </Nav.Item>

            <Nav.Link as={Link} to="/transaction-report" eventKey="/transaction-report" className="text-dark d-flex align-items-center">
                <BsFileEarmarkText className="me-2" /> Transaction report
            </Nav.Link>

            {isPublisher && (
                <Nav.Link as={Link} to="/reference-report" eventKey="/reference-report" className="text-dark d-flex align-items-center">
                    <BiNetworkChart className="me-2" /> Reference report
                </Nav.Link>
            )}

            {!isPublisher && (
                <Nav.Link as={Link} to="/publisher-report" eventKey="/publisher-report" className="text-dark d-flex align-items-center">
                    <BiBuilding className="me-2" /> Publisher report
                </Nav.Link>
            )}

            <Nav.Link as={Link} to="/campaign-report" eventKey="/campaign-report" className="text-dark d-flex align-items-center">
                <BiBarChartAlt2 className="me-2" /> Campaign report
            </Nav.Link>

            <Nav.Link as={Link} to="/click-report" eventKey="/click-report" className="text-dark d-flex align-items-center">
                <BiMouse className="me-2" /> Click report
            </Nav.Link>

            {isPublisher && (
                <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                    Publisher tools
                </Nav.Item>
            )}

            {isPublisher && user?.byClick?.enabled && (
                <Nav.Link as={Link} to="/link-script" eventKey="/link-script" className="text-dark d-flex align-items-center">
                    <ImMagicWand className="me-2" style={{ transform: "scaleX(-1)" }} /> Link automation
                </Nav.Link>
            )}

            {isPublisher && (
                <Nav.Link as={Link} to="/link-builder" eventKey="/link-builder" className="text-dark d-flex align-items-center">
                    <FaLink className="me-2" /> Link builder
                </Nav.Link>
            )}

            {user?.kind === "advertiser" && (
                <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                    Advertiser tools
                </Nav.Item>
            )}

            {(isPublisher || user?.kind === "advertiser") && (
                hasPartnershipsAccess ? (
                    <Nav.Link
                        as={Link}
                        to="/partnerships"
                        eventKey="/partnerships"
                        className="text-dark d-flex align-items-center"
                    >
                        <FaHandshake className="me-2" /> Partnerships
                    </Nav.Link>
                ) : (
                    <OverlayTrigger
                        placement="right"
                        overlay={
                            <Tooltip id="partnerships-disabled-tooltip">
                                {isPublisher
                                    ? "Create direct agreements with advertisers outside affiliate networks, covering sales, leads, and clicks. Contact info@nordpar.com if you are interested in learning more."
                                    : "Create direct agreements with publishers outside affiliate networks, covering sales, leads, and clicks. Contact info@nordpar.com if you are interested in learning more."}
                            </Tooltip>
                        }
                    >
                        <span className="d-block" tabIndex={0}>
                            <Nav.Link
                                eventKey="/partnerships"
                                className="text-muted d-flex align-items-center"
                                disabled
                                style={{ pointerEvents: "none" }}
                            >
                                <FaHandshake className="me-2" /> Partnerships
                            </Nav.Link>
                        </span>
                    </OverlayTrigger>
                )
            )}

            <Nav.Item className="mt-3 mb-1 ps-2 text-muted" style={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                Operations
            </Nav.Item>

            {!(user as any)?.feed?.disabled && (
                <Nav.Link as={Link} to="/feed-management" eventKey="/feed-management" className="text-dark d-flex align-items-center">
                    <FaRss className="me-2" /> Feed management
                </Nav.Link>
            )}

            <Nav.Link as={Link} to="/scheduled-reports" eventKey="/scheduled-reports" className="text-dark d-flex align-items-center">
                <FaRegFileAlt className="me-2" /> Scheduled reports
            </Nav.Link>

            <Nav.Link as={Link} to="/alerts" eventKey="/alerts" className="text-dark d-flex align-items-center">
                <FaRegBell className="me-2" /> Alerts
            </Nav.Link>

            <Nav.Link
                as={Link}
                to="/network-settings"
                eventKey="/network-settings"
                className="mt-auto text-dark d-flex align-items-center"
            >
                <FaCog className="me-2" />
                <span>Network settings</span>

                {!hasActiveCredentials && (
                    <FaExclamationTriangle
                        className="ms-auto text-warning"
                        title="Action required: network credentials missing"
                    />
                )}
            </Nav.Link>
        </>
    )

    return (
        <div className="d-sm-flex" style={{ width: "100%", maxWidth: "100%" }}>
            {!isPdf && (
                <>
                    <button
                        type="button"
                        className="d-md-none border shadow-sm rounded-circle p-0"
                        onClick={() => setIsMobileNavOpen((prev) => !prev)}
                        aria-label={isMobileNavOpen ? "Skjul menu" : "Vis menu"}
                        aria-expanded={isMobileNavOpen}
                        aria-controls="mobile-dashboard-nav"
                        style={{
                            width: "42px",
                            height: "42px",
                            position: "fixed",
                            bottom: "16px",
                            left: isMobileNavOpen ? "252px" : "12px",
                            zIndex: 1041,
                            color: "#212529",
                            backgroundColor: isMobileNavOpen ? "rgba(255, 255, 255, 0.6)" : "rgba(255, 255, 255, 0.35)",
                            borderColor: "rgba(33, 37, 41, 0.16)",
                            backdropFilter: "blur(10px) saturate(120%)",
                            WebkitBackdropFilter: "blur(10px) saturate(120%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "left 0.2s ease, background-color 0.2s ease",
                        }}
                    >
                        {isMobileNavOpen ? <FaTimes /> : <FaBars />}
                    </button>

                    {isMobileNavOpen && (
                        <div
                            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25 d-md-none"
                            style={{ zIndex: 1039 }}
                            onClick={() => setIsMobileNavOpen(false)}
                            aria-hidden="true"
                        />
                    )}

                    <Nav
                        id="mobile-dashboard-nav"
                        variant="pills"
                        className="dashboard-mobile-nav flex-column flex-nowrap rounded-end-3 bg-light-subtle d-md-none"
                        style={{
                            width: "240px",
                            minWidth: "240px",
                            height: "100dvh",
                            minHeight: 0,
                            zIndex: 1040,
                            padding: "0.5rem",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            transform: isMobileNavOpen ? "translateX(0)" : "translateX(-105%)",
                            transition: "transform 0.2s ease",
                            overflowY: "auto",
                            pointerEvents: isMobileNavOpen ? "auto" : "none",
                        }}
                        onSelect={handleSelect}
                        activeKey={activeTab}
                    >
                        {isAdmin ? adminNavLinks : defaultNavLinks}
                    </Nav>
                </>
            )}

            <Nav
                variant="pills"
                className={`dashboard-sidebar-nav flex-column flex-nowrap mt-2 mx-2 rounded-3 bg-light-subtle d-none ${
                    isPdf ? "" : "d-md-flex"
                }`}
                style={{
                    width: "240px",
                    minWidth: "240px",
                }}
                onSelect={handleSelect}
                activeKey={activeTab}
            >
                {isAdmin ? adminNavLinks : defaultNavLinks}
            </Nav>

            <div
                className={`flex-grow-1 ${isPdf ? "mt-0" : "mt-2"}`}
                style={{ minWidth: 0, maxWidth: "100%" }}
            >
                <Suspense fallback={
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "calc(100vh - 150px)", width: "100%" }}>
                        <Spinner animation="border" style={{ width: "3rem", height: "3rem" }} />
                        <div className="mt-3 text-muted fw-semibold">Loading data...</div>
                    </div>
                }>
                    {isAdmin ? (
                        activeTab === "/" ? (
                            <AdminHome />
                        ) : activeTab.startsWith("/eb-pristjek") ? (
                            <EBPristjek />
                        ) : activeTab === "/user-management" ? (
                            <UserManagement />
                        ) : activeTab === "/partnership-agreements" ? (
                            loc.pathname.startsWith("/partnership-agreements/")
                                ? <PartnershipAgreementDetail />
                                : <PartnershipAgreements />
                        ) : activeTab === "/conversions" ? (
                            <Conversions />
                        ) : activeTab === "/publisher-campaign-urls" ? (
                            <PublisherCampaignUrls />
                        ) : activeTab === "/network-credentials" ? (
                            <NetworkCredentials />
                        ) : (
                            "Not found"
                        )
                    ) : activeTab === "/" || activeTab === "/d" || activeTab.startsWith("/d/") ? (
                        <Home />
                    ) : activeTab === "/transaction-report" ? (
                        <TransactionReport />
                    ) : activeTab === "/reference-report" ? (
                        <ReferenceReport />
                    ) : activeTab === "/publisher-report" ? (
                        <PublisherReport />
                    ) : activeTab === "/campaign-report" ? (
                        <CampaignReport />
                    ) : activeTab === "/click-report" ? (
                        <ClickReport />
                    ) : activeTab === "/alerts" ? (
                        <Alerts />
                    ) : activeTab === "/feed-management" ? (
                        <Feed />
                    ) : activeTab === "/network-settings" ? (
                        <NetworkSettings />
                    ) : activeTab.startsWith("/link-script") ? (
                        <LinkScript />
                    ) : activeTab === "/link-builder" ? (
                        <LinkBuilder />
                    ) : activeTab === "/partnerships" && hasPartnershipsAccess ? (
                        <PartnershipsPage />
                    ) : activeTab === "/scheduled-reports" ? (
                        <ScheduledReports />
                    ) : (
                        "Not found"
                    )}
                </Suspense>
            </div>
        </div>
    )
}
